/**
 * Venice takes pictures, clips and audio inline, as base64 inside one JSON request. Its
 * front door refuses a request body of 32 MB or more, and that refusal carries no CORS
 * headers, so the browser only reports "Failed to fetch". These helpers keep requests
 * under the limit and say plainly when they cannot.
 */

/** Requests must be smaller than this (measured: 33,554,432 bytes is refused, one KB less is not). */
export const VENICE_MAX_REQUEST_BYTES = 32 * 1024 * 1024;
/** Kept free when fitting pictures, for the prompt and the other fields of the request. */
const HEADROOM_BYTES = 512 * 1024;
/** The field names and settings around the media, in the final check. */
const FIELDS_BYTES = 16 * 1024;
/** Venice turns away single files over 25 MB; a picture inline above this is made smaller anyway. */
const PICTURE_MAX_INLINE_BYTES = 20 * 1024 * 1024;
/** Venice's video models (Seedance, Kling) need the short side of a picture to be at least 300 px. */
const MIN_SHORT_SIDE = 300;

export interface ShrinkStep {
    longSide: number;
    quality: number;
}

/** Tried in turn on the largest pictures until the request fits. */
export const SHRINK_STEPS: ShrinkStep[] = [
    { longSide: 2048, quality: 0.9 },
    { longSide: 1536, quality: 0.86 },
    { longSide: 1024, quality: 0.82 },
];

export type ShrinkPicture = (dataUrl: string, step: ShrinkStep) => Promise<string | null>;

export const formatMegabytes = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

/** Bytes a string takes in the request; base64 is plain ASCII, so only the prompt can differ. */
export const requestBytes = (text: string): number => {
    let bytes = text.length;
    for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);
        if (code < 0x80) continue;
        if (code < 0x800) bytes += 1;
        else if (code >= 0xd800 && code <= 0xdbff) { bytes += 2; i++; } // a surrogate pair is 4 bytes for 2 units
        else bytes += 2;
    }
    return bytes;
};

/** Decodes a base64 data URL without fetch, which the site's security policy does not allow for data URLs. */
export const dataUrlToBlob = (dataUrl: string): Blob => {
    const comma = dataUrl.indexOf(',');
    const header = dataUrl.slice(5, comma);
    const type = header.split(';')[0] || 'application/octet-stream';
    const body = dataUrl.slice(comma + 1);
    if (!header.includes(';base64')) return new Blob([decodeURIComponent(body)], { type });
    const binary = atob(body);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type });
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error('Could not read the picture.'));
        reader.readAsDataURL(blob);
    });

/** Size of a picture redrawn with its long side at most `longSide`, keeping the short side usable. */
export const shrunkSize = (width: number, height: number, longSide: number): { width: number; height: number } => {
    const long = Math.max(width, height);
    const short = Math.min(width, height);
    let scale = Math.min(1, longSide / long);
    if (short * scale < MIN_SHORT_SIDE) scale = Math.min(1, MIN_SHORT_SIDE / short);
    return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

/**
 * Redraws a picture as a JPEG. Transparent parts become white, the usual backdrop of a
 * reference sheet. Returns null when the browser cannot read the picture.
 */
export const shrinkPicture: ShrinkPicture = async (dataUrl, step) => {
    let bitmap: ImageBitmap | null = null;
    try {
        bitmap = await createImageBitmap(dataUrlToBlob(dataUrl));
        const { width, height } = shrunkSize(bitmap.width, bitmap.height, step.longSide);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bitmap, 0, 0, width, height);
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', step.quality));
        return blob ? await blobToDataUrl(blob) : null;
    } catch {
        return null;
    } finally {
        bitmap?.close();
    }
};

/** Room the pictures have once the clips, audio and prompt are counted. */
export const pictureBudget = (otherBytes: number): number => VENICE_MAX_REQUEST_BYTES - HEADROOM_BYTES - otherBytes;

/**
 * A picture larger than this is sent redrawn as a reference needs it (see referenceStep), even
 * when the request would fit: tens of MB of full-size PNGs can take longer to upload on a slow
 * connection than Venice waits for a request (about 100 s), and then fail as "could not reach".
 */
export const LIGHT_PICTURE_BYTES = 1536 * 1024;

/**
 * How a reference picture is sent: a JPEG of high quality, its long side at most what the result
 * needs (2048 px, or the long side of a larger result, as 4096 for a 4K picture).
 */
export const referenceStep = (resultLongSide = 2048): ShrinkStep => ({ longSide: Math.max(2048, resultLongSide), quality: 0.92 });

/** Requests sent at the same time together upload no more than this; past it they go one after another */
export const PARALLEL_UPLOAD_BYTES = 24 * 1024 * 1024;

/**
 * Makes pictures smaller, largest first, until they fit in what the request has left after
 * `otherBytes`. With `light`, every picture over LIGHT_PICTURE_BYTES is first redrawn with it,
 * so the request is quick to upload; without it, pictures are left as they are when everything
 * already fits. The result may still be too large when clips or audio take most of the room;
 * check it with `oversizedRequest`.
 */
export const fitPicturesToRequest = async (
    pictures: string[],
    otherBytes: number,
    shrink: ShrinkPicture = shrinkPicture,
    onShrinking?: () => void,
    light?: ShrinkStep,
): Promise<string[]> => {
    const out = [...pictures];
    const budget = pictureBudget(otherBytes);
    const total = () => out.reduce((sum, picture) => sum + picture.length, 0);
    let told = false;
    const replace = async (i: number, step: ShrinkStep) => {
        if (!told) { told = true; onShrinking?.(); }
        const smaller = await shrink(out[i], step);
        if (smaller && smaller.length < out[i].length) out[i] = smaller;
    };
    for (let i = 0; i < out.length; i++) {
        if (light && out[i].length > LIGHT_PICTURE_BYTES) await replace(i, light);
        if (out[i].length > PICTURE_MAX_INLINE_BYTES) await replace(i, SHRINK_STEPS[0]);
    }
    for (const step of SHRINK_STEPS) {
        if (total() <= budget) break;
        const largestFirst = out.map((_, i) => i).sort((a, b) => out[b].length - out[a].length);
        for (const i of largestFirst) {
            if (total() <= budget) break;
            await replace(i, step);
        }
    }
    return out;
};

export interface RequestPart {
    label: string;
    bytes: number;
}

/** Null when the parts, prompt included, fit in one request; otherwise a message naming the largest of them. */
export const oversizedRequest = (parts: RequestPart[]): string | null => {
    const total = parts.reduce((sum, part) => sum + part.bytes, 0) + FIELDS_BYTES;
    if (total < VENICE_MAX_REQUEST_BYTES) return null;
    const largest = [...parts]
        .sort((a, b) => b.bytes - a.bytes)
        .slice(0, 3)
        .map(part => `“${part.label}” ${formatMegabytes(part.bytes)}`)
        .join(', ');
    return `The references add up to ${formatMegabytes(total)}, and Venice accepts at most 32 MB in one request. ` +
        `Largest: ${largest}. Use a shorter part of a clip, shorter audio, or fewer references.`;
};

/** Room kept for the pictures when clips are sized; they are made smaller to fit in it if need be. */
const PICTURE_RESERVE_BYTES = 4 * 1024 * 1024;
/** Below this a reference clip is too blurred to be worth sending. */
const MIN_CLIP_BITS_PER_SECOND = 1_000_000;
/** The browser's recorder can go a little over the rate it is asked for. */
const RECORDER_OVERSHOOT = 0.85;

/**
 * The highest bitrate reference clips can be recorded at so that the request stays under
 * Venice's cap: clips cannot be made smaller afterwards the way pictures can, so they get
 * what is left after the audio (`otherBytes`, as sent: base64) and a share kept for pictures.
 * Long selections (Seedance 2.5 takes 30 s) get a lower rate; short ones are not limited.
 */
export const clipBitrateBudget = (totalClipSec: number, otherBytes = 0): number => {
    const room = Math.max(0, VENICE_MAX_REQUEST_BYTES - HEADROOM_BYTES - PICTURE_RESERVE_BYTES - otherBytes);
    const rawBytes = (room * 3) / 4; // base64 adds a third
    const bitsPerSecond = (rawBytes * 8 * RECORDER_OVERSHOOT) / Math.max(1, totalClipSec);
    return Math.max(MIN_CLIP_BITS_PER_SECOND, Math.floor(bitsPerSecond));
};
